/** Hover-preview placement for the order-pull walk list.

Desktop CSS shows a 280×392 card (about 63:88). Phone CSS stays 120×168.
``cardPreviewOrigin`` only decides the viewport origin.
*/

export const DESKTOP_PREVIEW_WIDTH = 280;
export const DESKTOP_PREVIEW_HEIGHT = 392;
export const PHONE_PREVIEW_WIDTH = 120;
export const PHONE_PREVIEW_HEIGHT = 168;
export const PREVIEW_GAP = 4;
export const PREVIEW_MARGIN = 8;

export interface PreviewAnchor {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface PreviewSize {
  width: number;
  height: number;
}

export interface PreviewViewport {
  width: number;
  height: number;
}

const SCRYFALL_SMALL = /^(https:\/\/cards\.scryfall\.io\/)small\//i;

/** Prefer an explicit larger URL. Otherwise upgrade a Scryfall ``small`` URL to ``normal``. */
export function higherResCardImageUrl(imageUrl: string, previewUrl?: string | null): string {
  const explicit = (previewUrl || '').trim();
  if (explicit) {
    return explicit;
  }
  return imageUrl.trim().replace(SCRYFALL_SMALL, '$1normal/');
}

/**
 * Place the preview just under the thumbnail, left-aligned.
 * Shift left when the right edge would leave the viewport, flip above when
 * the bottom would, and clamp to the margin when neither side has room.
 */
export function cardPreviewOrigin(
  anchor: PreviewAnchor,
  preview: PreviewSize,
  viewport: PreviewViewport,
  margin = PREVIEW_MARGIN,
  gap = PREVIEW_GAP,
): { left: number; top: number } {
  let left = anchor.left;
  if (left + preview.width > viewport.width - margin) {
    left = viewport.width - margin - preview.width;
  }
  if (left < margin) {
    left = margin;
  }

  let top = anchor.bottom + gap;
  const overflowsBelow = top + preview.height > viewport.height - margin;
  const roomAbove = anchor.top - gap - preview.height >= margin;
  if (overflowsBelow && roomAbove) {
    top = anchor.top - gap - preview.height;
  } else if (overflowsBelow) {
    top = Math.max(margin, viewport.height - margin - preview.height);
  }
  if (top < margin) {
    top = margin;
  }
  return { left, top };
}
